import { MessageComposerDialog } from "@/components/ui/message-composer-dialog";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/components/ui/use-toast";
import { useSendInternalOpsDirectMessage } from "@/lib/api/internal-ops-messaging";
import { useMentorPerformanceEvent } from "@/lib/hooks/mentor-performance/use-mentor-performance-event";
import { PostHogEvents } from "@/lib/posthog-events";
import { useNavigate } from "@tanstack/react-router";

interface SendMessageModalProps {
  mentorId: string;
  mentorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SendMessageModal({
  mentorId,
  mentorName,
  open,
  onOpenChange,
}: SendMessageModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { track } = useMentorPerformanceEvent();
  const { mutateAsync: sendMessage, isPending } =
    useSendInternalOpsDirectMessage(mentorId);

  const handleSend = async (message: string) => {
    try {
      const result = await sendMessage({ message });
      toast({
        title: "Message sent",
        description: `Your message to ${mentorName} was sent.`,
        action: (
          <ToastAction
            altText="View message in support chat"
            className="h-auto whitespace-normal px-3 py-1.5 text-left"
            onClick={() => {
              track(
                PostHogEvents.MENTOR_DASHBOARD_SUPPORT_CHAT_MESSAGE_VIEWED,
                {
                  mentor_id: mentorId,
                },
              );
              navigate({
                to: "/conversations",
                search: {
                  segment: "support",
                  internalChannelId: result.channelId,
                },
              });
            }}
          >
            View message
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({
        title: "Couldn't send message",
        description: "Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <MessageComposerDialog
      open={open}
      title="Send Message to Mentor"
      placeholder={`Write a message to ${mentorName}`}
      submitLabel="Send Message"
      isPending={isPending}
      contentClassName="gap-6 rounded-lg border-0 bg-surfaces-z0 font-body"
      titleClassName="font-heading text-header-3"
      inputClassName="min-h-36 border-buttonBorder-tertiary text-body-1"
      onOpenChange={onOpenChange}
      onSubmit={handleSend}
    />
  );
}
